import React, { FC } from "react";

import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { DatasetScatterProps, LagLeadLoadingConfig } from "components/dashboard/charts/interfaces";
import { T } from "services/lang/LanguageService";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import TimeUnitDropdown from "../dropdowns/time-unit-dropdown/TimeUnitDropdown";
import InputLabelWrapper from "../input-label-wrapper/InputLabelWrapper";

const DatasetChartConfig: FC<DashboardEditorProps<DatasetScatterProps>> = ({
  config,
  onConfigChange,
}) => {
  const bubbleChart = config?.bubbleChart || false;
  const handleLoadConfChange = (
    field: keyof LagLeadLoadingConfig,
    value: string | number | null
  ) => {
    if (value === null && config.loadConf) {
      const { [field]: remove, ...rest } = config.loadConf;
      onConfigChange({ ...config, loadConf: rest as LagLeadLoadingConfig });
      return;
    }
    onConfigChange({
      ...config,
      loadConf: { ...config?.loadConf, [field]: value! } as LagLeadLoadingConfig,
    });
  };

  const handleChange = (
    field: keyof DatasetScatterProps,
    value: string | string[] | number | boolean | null
  ) => {
    if (value === null) {
      const { [field]: remove, ...rest } = config;
      onConfigChange({ ...rest });
      return;
    }
    onConfigChange({ ...config, [field]: value });
  };

  return (
    <div className="grid p-fluid">
      <InputLabelWrapper labelName={T("load-conf.lag-offset")} col={3}>
        <div className="p-inputgroup">
          <InputNumber
            value={config.loadConf?.lag ?? 6}
            className="w-4"
            style={{ flexGrow: "0.5" }}
            onValueChange={e => handleLoadConfChange("lag", e.value ?? 0)}
          />
          <TimeUnitDropdown
            className="p-inputgroup-addon p-0 w-8"
            value={config?.loadConf?.lagUnit ?? "HOURS"}
            onChange={e => handleLoadConfChange("lagUnit", e.value?.toString().toUpperCase())}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("load-conf.lead-offset")} col={3}>
        <div className="p-inputgroup">
          <InputNumber
            value={config?.loadConf?.lead ?? 0}
            className="w-4"
            style={{ flexGrow: "0.5" }}
            onValueChange={e => handleLoadConfChange("lead", e.value ?? 0)}
          />
          <TimeUnitDropdown
            className="p-inputgroup-addon p-0 w-8"
            value={config?.loadConf?.leadUnit ?? "HOURS"}
            onChange={e => handleLoadConfChange("leadUnit", e.value?.toString().toUpperCase())}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("load-conf.align-unit")} col={3}>
        <TimeUnitDropdown
          value={config?.loadConf?.alignUnit ?? "MINUTES"}
          onChange={e => handleLoadConfChange("alignUnit", e.value?.toString().toUpperCase())}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("load-conf.refresh-time")} col={3}>
        <InputNumber
          value={config?.updateFrequency ?? 0}
          onChange={e => handleChange("updateFrequency", e.value)}
          suffix=" s"
          showButtons
        />
      </InputLabelWrapper>
      {!bubbleChart && (
        <InputLabelWrapper labelName={T("chart-config.time-axis-step")} col={2}>
          <InputNumber
            value={config?.timeAxisStepSeconds}
            onChange={e => handleChange("timeAxisStepSeconds", e.value)}
            suffix=" s"
            showButtons
          />
        </InputLabelWrapper>
      )}
      <InputLabelWrapper labelName={T("word.height")} col={2}>
        <InputNumber
          value={(config?.height as number) ?? 400}
          suffix=" px"
          onChange={e => handleChange("height", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("word.width")} col={2}>
        <InputNumber
          value={config?.width as number}
          suffix=" px"
          onChange={e => handleChange("width", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={T("word.dynamic-height")}
        col={2}
        className="align-items-center gap-3"
      >
        <InputSwitch
          checked={Boolean(config?.dynamicHeight)}
          onChange={e => handleChange("dynamicHeight", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={T("action.show-date")}
        col={2}
        className="align-items-center gap-3"
      >
        <InputSwitch
          checked={config?.showDateSelection ?? true}
          onChange={e => handleChange("showDateSelection", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={T("chart-config.hide-refresh")}
        col={2}
        className="align-items-center gap-3"
      >
        <InputSwitch
          checked={Boolean(config?.hideRefresh)}
          onChange={e => handleChange("hideRefresh", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={T("label.scatter.trend-lines")}
        col={2}
        className="align-items-center gap-3"
      >
        <InputSwitch
          checked={config?.trendLines}
          onChange={e => handleChange("trendLines", e.value)}
        />
      </InputLabelWrapper>
    </div>
  );
};

export default DatasetChartConfig;
